import { Node } from "@tiptap/pm/model";
import { Editor } from "@tiptap/react";

import {
  CheckForUnderstandingQuizAttachment,
  ICheckForUnderstandingQuestion,
} from "@/types/models/CheckForUnderstandingQuiz.interface";
import getChecksForUnderstandingLevelMatch from "@/view/utils/getChecksForUnderstandingLevelMatch";
import {
  checkForUnderstandingNodeSize,
  CHECK_FOR_UNDERSTANDING_BLOCK,
} from "../ChecksForUnderstanding";

export const getChecksForUnderstandingData = (
  quizAttachmentData: CheckForUnderstandingQuizAttachment,
  levelId: string,
) => {
  const quiz = getChecksForUnderstandingLevelMatch(quizAttachmentData, levelId);
  const { questions = [] } = quiz || {};

  const uniqueQuestions: {
    [key: string]: {
      id: string;
      prosemirror_insert_index: number;
      question: string;
      options: Array<{ id: string; text: string; is_correct: boolean }>;
    };
  } = {};

  for (const data of questions) {
    if (!uniqueQuestions[data.question]) {
      uniqueQuestions[data.question] = data;
    }
  }

  const checksForUnderstandingData: ICheckForUnderstandingQuestion[] = [];
  let idx = 0;

  // construct checksForUnderstandingData after removing duplicate questions so that we know the total number of unique questions
  for (const question of Object.values(uniqueQuestions)) {
    checksForUnderstandingData.push({
      ...question,
      question_number: idx + 1,
      total_number_of_questions: Object.values(uniqueQuestions).length,
    });
    idx++;
  }

  return checksForUnderstandingData;
};
export const getClosestIndexOfTextBlock = (
  doc: Node,
  index: number,
): number => {
  // Clamp index to document bounds to prevent RangeError when the insertion
  // index (which may include accumulated offsets from previously inserted CFU
  // nodes) exceeds the document's content size.
  const clampedIndex = Math.min(index, doc.content.size);
  let adjustedPosition: null | number = null;
  // Backwards search, between the beginning of the document and index.
  // doc.nodesBetween cannot be run in revers, so we'll have to keep overriding adjustedPosition with the
  // last override being the closest to the index.
  doc.nodesBetween(0, clampedIndex, (node: Node, idx: number) => {
    if (node.isTextblock) {
      const isEmptyParagraph =
        node.type.name === "paragraph" && node.content.size === 0;
      // There is an issue when attempting to insert into an empty paragraph in a prosemirror document.
      // Because of it, we need to move past that empty paragraph by incrementing idx by 1.
      adjustedPosition = idx + (isEmptyParagraph ? 1 : node.content.size);
    }
  });
  if (adjustedPosition !== null) return adjustedPosition;
  // In the very unlikely event of the backwards search not returning anything,
  // try searching forward from the current position
  doc.nodesBetween(
    clampedIndex,
    doc.content.size,
    (node: Node, idx: number) => {
      const isEmptyParagraph =
        node.type.name === "paragraph" && node.content.size === 0;
      // Stop as soon as a valid position is found.
      if (node.isTextblock && !adjustedPosition) {
        adjustedPosition = idx + (isEmptyParagraph ? 1 : node.content.size);
      }
      return undefined;
    },
  );
  if (adjustedPosition !== null) return adjustedPosition;
  throw Error(
    "Invalid Checks For Understanding insertion index: Could not find any valid text blocks in the document for",
  );
};

export const insertChecksForUnderstanding = ({
  editor,
  quizAttachmentData,
  assignmentId,
  contentId,
  levelId,
  onInsertionComplete,
}: {
  editor: Editor;
  quizAttachmentData: CheckForUnderstandingQuizAttachment;
  assignmentId: string;
  contentId: string;
  levelId: string;
  onInsertionComplete: (editor: Editor) => void;
}) => {
  const $checkpoints = editor?.$nodes(CHECK_FOR_UNDERSTANDING_BLOCK);
  const questions = getChecksForUnderstandingData(quizAttachmentData, levelId);

  const hasUninsertedCheckpoints =
    questions?.length && $checkpoints?.length === 0;
  if (editor && hasUninsertedCheckpoints && Boolean(contentId)) {
    try {
      questions.forEach(
        (question: ICheckForUnderstandingQuestion, index: number) => {
          // Offset takes into account previously inserted Check for understanding nodes.
          const offset = index * checkForUnderstandingNodeSize;
          const insertionIndex = getClosestIndexOfTextBlock(
            editor.view.state.doc,
            question?.prosemirror_insert_index + offset,
          );
          editor
            .chain()
            .setTextSelection(insertionIndex)
            .selectTextblockEnd()
            .insertContent({
              type: CHECK_FOR_UNDERSTANDING_BLOCK,
              attrs: {
                quiz: {
                  questionData: question,
                  quizId: quizAttachmentData.id,
                  assignmentId,
                  contentId,
                },
              },
            })
            .run();
        },
      );
    } catch (error) {
      const errorMessage =
        "Checks for Understanding nodes insertion error: " +
        JSON.stringify(error);
      console.error(errorMessage);
    }
  }
  // Running this callback will ensure that a ProseMirror transaction is dispatched after the final tree modification
  // to ensure that all react components re-render with all the checks for understanding in place.
  if (editor) onInsertionComplete(editor);
};
